// src/pages/admin/AdminUsersPage.tsx
import React, { useEffect, useState } from 'react';
import {
    AdminUser,
    AdminCompany,
    Role,
    listUsers,
    listCompanies,
    listRoles,
    updateUser,
    deleteUser,
} from '../../features/admin/api';
import { useToast } from 'src/shared/ui/toast/useToast';

const AdminUsersPage: React.FC = () => {
    const toast = useToast();

    const [rows, setRows] = useState<AdminUser[]>([]);
    const [loading, setLoading] = useState(false);

    const [companies, setCompanies] = useState<AdminCompany[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [companyFilter, setCompanyFilter] = useState<string>('ALL');
    const [roleFilter, setRoleFilter] = useState<string>('ALL');

    const fetchUsers = async () => {
        setLoading(true);
        try {
            // const list = await listUsers();
            // setRows(list);
            const [users, comps, rs] = await Promise.all([listUsers(), listCompanies(), listRoles()]);
            setRows(users);
            setCompanies(comps);
            setRoles(rs.filter((r) => r.scope === 'COMPANY')); // 회사 멤버십 필터용
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleToggleLock = async (user: AdminUser) => {
        const nextStatus = user.status === 'ACTIVE' ? 'LOCKED' : 'ACTIVE';
        setLoading(true);
        try {
            const updated = await updateUser(user.id, { status: nextStatus });
            // setRows((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
            setRows((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
            toast.success(`${updated.email} 사용자를 ${nextStatus === 'LOCKED' ? '잠금' : '잠금 해제'}했습니다.`, {
                title: '처리 완료',
            });
        } catch (err: any) {
            toast.error(err?.response?.data?.error?.message ?? '사용자 상태 변경 중 오류가 발생했습니다.', {
                title: '처리 실패',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (user: AdminUser) => {
        if (!window.confirm(`정말로 ${user.email} 사용자를 삭제하시겠습니까?`)) return;
        setLoading(true);
        try {
            await deleteUser(user.id);
            setRows((prev) => prev.filter((u) => u.id !== user.id));
            toast.success(`${user.email} 사용자를 삭제했습니다.`, {
                title: '삭제 완료',
            });
        } catch (err: any) {
            toast.error('삭제할 수 없습니다. (프로젝트 등과 연결되어 있을 수 있습니다)', {
                title: '삭제 실패',
            });
        } finally {
            setLoading(false);
        }
    };

    const filteredRows = rows.filter((u) => {
        const ms = u.companyMemberships ?? [];
        if (companyFilter !== 'ALL') {
            if (!ms.some((m) => m.company.id === companyFilter)) return false;
        }
        if (roleFilter !== 'ALL') {
            if (!ms.some((m) => m.role.id === roleFilter)) return false;
        }
        return true;
    });

    return (
        <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold text-gray-900">System Users</h1>
                {loading && <span className="text-xs text-gray-500">Loading...</span>}
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <select
                    className="border rounded px-2 py-1 text-sm bg-white"
                    value={companyFilter}
                    onChange={(e) => setCompanyFilter(e.target.value)}
                >
                    <option value="ALL">All Companies</option>
                    {companies.map((c) => (
                        <option key={c.id} value={c.id}>
                            {c.name}
                        </option>
                    ))}
                </select>

                <select
                    className="border rounded px-2 py-1 text-sm bg-white"
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                >
                    <option value="ALL">All Roles</option>
                    {roles.map((r) => (
                        <option key={r.id} value={r.id}>
                            {r.name}
                        </option>
                    ))}
                </select>

                <button
                    className="text-sm text-gray-600 hover:underline"
                    onClick={() => {
                        setCompanyFilter('ALL');
                        setRoleFilter('ALL');
                    }}
                >
                    Reset
                </button>
            </div>

            <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
                <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wide">
                        <tr>
                            <th className="px-3 py-2 text-left">Name</th>
                            <th className="px-3 py-2 text-left">Email</th>
                            <th className="px-3 py-2 text-left">Company</th>
                            <th className="px-3 py-2 text-left">Role</th>
                            <th className="px-3 py-2 text-left">Status</th>
                            <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={6} className="px-3 py-6 text-center text-gray-400">
                                    아직 등록된 사용자가 없습니다.
                                </td>
                            </tr>
                        )}
                        {filteredRows.map((u) => (
                            <tr key={u.id} className="border-t">
                                <td className="px-3 py-2">{u.name}</td>
                                <td className="px-3 py-2">{u.email}</td>
                                {/* Company column */}
                                <td className="px-3 py-2 text-xs text-gray-700">
                                    {(u.companyMemberships ?? []).length === 0 ? (
                                        <span className="text-gray-400">-</span>
                                    ) : (
                                        <div className="space-y-1">
                                            {(u.companyMemberships ?? []).map((m) => (
                                                <div key={m.id} className="leading-tight">
                                                    {m.company.name}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </td>

                                {/* Role column */}
                                <td className="px-3 py-2 text-xs text-gray-700">
                                    {(u.companyMemberships ?? []).length === 0 ? (
                                        <span className="text-gray-400">-</span>
                                    ) : (
                                        <div className="space-y-1">
                                            {(u.companyMemberships ?? []).map((m) => (
                                                <div key={m.id} className="leading-tight">
                                                    {m.role.name}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </td>
                                <td className="px-3 py-2">
                                    <span
                                        className={
                                            'inline-flex rounded-full px-2 py-0.5 text-[11px] ' +
                                            (u.status === 'ACTIVE'
                                                ? 'bg-emerald-50 text-emerald-700'
                                                : 'bg-rose-50 text-rose-700')
                                        }
                                    >
                                        {u.status}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-right space-x-2">
                                    <button
                                        className="text-xs text-rose-600 hover:underline"
                                        onClick={() => handleToggleLock(u)}
                                        title="사용자의 로그인 권한등을 제한합니다."
                                    >
                                        {u.status === 'ACTIVE' ? '잠금' : '잠금 해제'}
                                    </button>
                                    <button
                                        className="text-xs text-gray-600 hover:underline"
                                        onClick={() => handleDelete(u)}
                                    >
                                        삭제
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminUsersPage;
